import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { observer } from 'mobx-react-lite';

import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useViewport from 'src/hooks/use-viewport';

import { formatCurrency } from 'shared/helpers/utils';
import ExitIcon from 'shared/assets/exit-icon';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import { SuccessFill } from 'shared/assets/success-fill';
import { Button } from 'src/components/core';
import { CartItem } from 'src/components/cart-item';
import { Offer } from 'src/components/special-card/offer';

export const CartInterstitial = observer(
  (): JSX.Element => {
    const theme = useTheme();
    const { closeCartInterstitial, openCartDrawer, isEmbedded } = useUI();
    const { subtotal, mostRecentItem, discountGamificationDetails, dispensary } = useCart();
    const hideDiscounts = useHideDiscountFromConsumer(dispensary);
    const { item, wasItemAlreadyInCart } = mostRecentItem;
    const quantityPrefix = wasItemAlreadyInCart ? 'Qty In Cart' : undefined;

    const { height, offset, parentOffset } = useViewport();

    const flags = useFlags();
    const isGamificationFlagEnabled = flags['consumer.ecomm.gamification.rollout'] ?? false;

    const matchingOffer = discountGamificationDetails?.find(
      (offer) =>
        offer.thresholds.length === 1 &&
        offer.thresholds[0].applicableProducts.some((product) => product.productKey === item.key)
    );

    const closeAndOpenCartDrawer = (): void => {
      closeCartInterstitial();
      openCartDrawer();
    };

    return (
      <Container
        $height={height}
        $isEmbedded={isEmbedded}
        $offset={offset}
        $parentOffset={parentOffset}
        data-testid='cart-interstitial'
      >
        <Wrapper>
          <Header>
            <SuccessFill fill={theme.customized.colors.buttonsLinks} />
            <Heading>Added to Cart</Heading>
            <CloseButton onClick={closeCartInterstitial} data-testid='close-button'>
              <ExitIcon height={20} width={20} />
              <VisuallyHidden>Close</VisuallyHidden>
            </CloseButton>
          </Header>

          <CartItem item={item} quantityPrefix={quantityPrefix} />

          {isGamificationFlagEnabled && matchingOffer && !hideDiscounts && (
            <OfferWrapper>
              <ProductIsIncludedInOffer>This product is included in a special offer</ProductIsIncludedInOffer>
              <Offer
                specialId={matchingOffer.specialId}
                specialName={matchingOffer.specialName}
                thresholdType={matchingOffer.thresholds[0].thresholdType}
                threshold={matchingOffer.thresholds[0].threshold}
                currentAmount={matchingOffer.thresholds[0].currentAmount}
                cartItemIds={matchingOffer.thresholds[0].applicableProducts}
                shouldDisplayItems
              />
            </OfferWrapper>
          )}

          <Footer>
            <StyledButton onClick={closeAndOpenCartDrawer} data-testid='review-cart-and-checkout-button'>
              Review Cart And Checkout - {formatCurrency(subtotal)}
            </StyledButton>
            <StyledButton
              variant='outlinePrimary'
              onClick={closeCartInterstitial}
              data-testid='continue-shopping-button'
            >
              Continue Shopping
            </StyledButton>
          </Footer>
        </Wrapper>
      </Container>
    );
  }
);

const Container = styled.div`
  ${({
    $height,
    $isEmbedded,
    $offset,
    $parentOffset,
  }: {
    $height: number;
    $isEmbedded: boolean;
    $offset: number;
    $parentOffset: number;
  }) => css`
    ${$isEmbedded &&
    css`
      height: ${$height - $offset}px;
      position: relative;
      top: ${$parentOffset + $offset}px;
    `}
  `}
`;

const Wrapper = styled.div`
  background: #fff;
  box-shadow: 0 0 20px 0 #00000026;

  @media (min-width: 440px) {
    border-radius: 28px;
    margin: 40px 20px 0 0;
    width: 400px;
  }
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid #e3e7e9;
  display: flex;
  gap: 16px;
  padding: 16px;
`;

const Heading = styled.h2`
  color: #121516;
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
`;

const CloseButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    fill: #485055;
  }
`;

const OfferWrapper = styled.div`
  margin: 0 16px;
`;

const ProductIsIncludedInOffer = styled.div`
  background: #00624a;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  color: #fff;
  font-size: 13px;
  line-height: 1;
  margin-bottom: 4px;
  padding: 8px;
  text-align: center;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  line-height: ${20 / 14};
  padding: ${({ variant }) => (variant === 'outlinePrimary' ? '14px 8px' : '16px 8px')};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 24px;
`;
